import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { get, merge } from 'lodash';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { getVisibleItems } from 'resolvers/getVisibleEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberDetailsPrescriptionsEntityWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberDetailsPrescriptionsEntityWidgetService } from 'services/memberDetailsPrescriptionsEntityWidget';
 *
 * // create service instance and send/cancel request
 * const memberDetailsPrescriptionsEntityWidgetService = new MemberDetailsPrescriptionsEntityWidgetService();
 * const result = await memberDetailsPrescriptionsEntityWidgetService.fetchData( '<user-id>' ); // fetch data
 * memberDetailsPrescriptionsEntityWidgetService.cancel(); // cancel an active request
 */
export class MemberDetailsPrescriptionsEntityWidgetService extends HttpService {

    // make request and fetch data
    async fetchData( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );
            
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component', {} ),
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // make request and fetch data
    async newOrder( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component.data', SERVICE_RESPONSE_STATUS.ERROR ),
            };
            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make request and fetch data
    async enableAuto( { url = {}, data = {} } = {} ) {
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } )
            } );

            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                return {
                    _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                };
            }

            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                _data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };
            
        } catch( error ) {
            return {
                _status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // initiates the disable auto refill request
    async disableAutoData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const memberId = data.data.memberId;

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            
            data: merge( data, {
                context: {
                    callId,
                    callType,
                    memberId,
                }
            } ),
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', [] ),
                ...get( response, 'body.component.structure', [] )
            }
        };
    }
    
    // make request and fetch data
    async fetchToDoWidgetInfo ( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    options: get( response, 'body.component.data.options', {} ),
                    state: get( response, 'body.component.data.state', {} ),
                    ...get( response, 'body.component.structure', {} ),
                }
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // fetch edit next refill date widget info
    async fetchEditNextRefillDateInfo( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    values: get( response, 'body.component.data.state.values' ),
                    title: get( response, 'body.component.structure.title' ),
                    description: get( response, 'body.component.structure.description' ),
                    fields: getVisibleItems( get( response, 'body.component.structure.fields' ), get( response, 'body.component.visibility' ) ),
                    actions: get( response, 'body.component.structure.actions' )
                }
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // submit next refill date change request
    async submitChangeNextRefillDate( { url, data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data' )
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // fetch live status for every prescription currently displayed, bypassing ES
    async fetchLivePrescriptionsStatus( { data } ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: '/member/prescription/fetch-live-prescriptions-status',
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component', {} ),
            };
        } catch( errResponse ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                // HttpService throws HttpServiceError with { status: <httpCode> } on non-2xx responses
                httpStatus: get( errResponse, 'status', 0 ),
                data: null,
            };
        }
    }
}
